import { OnboardingStep, UseLocalStorageReturn } from '@helpers/Models';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook for managing localStorage operations
 * Provides utilities for managing onboarding steps and other localStorage data
 */
export const useLocalStorage = (): UseLocalStorageReturn => {
  const navigate = useNavigate();

  /**
   * Updates the isComplete status of a specific onboarding step
   *
   * @param stepsId - The stepsId of the onboarding step to update
   * @returns boolean - Returns true if update was successful, false otherwise
   */
  const updateOnboardingStep = (stepsId: number): boolean => {
    try {
      console.log(`Updating onboarding step with stepsId: ${stepsId}`);
      // Get current onboarding steps from localStorage
      const onboardingStepsData = localStorage.getItem('onboardingSteps');

      if (!onboardingStepsData) {
        console.warn('No onboarding steps found in localStorage');
        return false;
      }

      // Parse the JSON data
      const onboardingSteps: OnboardingStep[] = JSON.parse(onboardingStepsData);

      // Find the step with the matching stepsId
      const stepIndex = onboardingSteps.findIndex(
        (step) => step.stepsId === stepsId,
      );

      if (stepIndex === -1) {
        console.warn(`No step found with stepsId: ${stepsId}`);
        return false;
      }

      // Update the isComplete status to 1
      onboardingSteps[stepIndex].isComplete = 1;

      // Save the updated data back to localStorage
      localStorage.setItem('onboardingSteps', JSON.stringify(onboardingSteps));

      console.log(`Successfully updated step ${stepsId} to complete`);
      return true;
    } catch (error) {
      console.error('Error updating onboarding step:', error);
      return false;
    }
  };

  /**
   * Attempts to resume the onboarding process by navigating to the first
   * incomplete onboarding step stored in `localStorage`.
   *
   * - Reads `onboardingSteps` from localStorage.
   * - If no steps exist, logs a warning and returns `false`.
   * - If steps exist, finds the first step where `isComplete === false`
   *   and navigates to its associated `route`.
   * - If all steps are complete, no navigation occurs.
   *
   * @returns {boolean | void}
   * - `false` if no onboarding steps are found in localStorage.
   * - `void` if onboarding steps exist - navigation occurs.
   *
   */
  const fetchResumeOnboardingStepId = (): boolean | void => {
    // Get current onboarding steps from localStorage
    const onboardingStepsData = localStorage.getItem('onboardingSteps');

    if (!onboardingStepsData) {
      console.warn('No onboarding steps found in localStorage');
      return false;
    }

    // Parse the JSON data
    const onboardingSteps: OnboardingStep[] = JSON.parse(onboardingStepsData);

    for (const step of onboardingSteps) {
      if (!step.isComplete) {
        navigate(step.route);
        break;
      }
    }
  };

  return {
    updateOnboardingStep,
    fetchResumeOnboardingStepId,
  };
};

export type { OnboardingStep };
